/**
 * Contexte pour la coordination des composants flottants
 *
 * @description Évite les chevauchements entre le chat et les autres éléments flottants
 * @since 2025-11-05
 */

import { createContext, useContext, useState, ReactNode } from 'react';

interface FloatingComponentsState {
  chatOpen: boolean;
  chatMinimized: boolean;
}

interface FloatingComponentsContextType {
  state: FloatingComponentsState;
  setChatOpen: (open: boolean) => void;
  setChatMinimized: (minimized: boolean) => void;
  getOptimalChatPosition: () => {
    bottom: string;
    right: string;
    maxWidth: string;
  };
}

const FloatingComponentsContext = createContext<FloatingComponentsContextType | undefined>(undefined);

export function FloatingComponentsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FloatingComponentsState>({
    chatOpen: false,
    chatMinimized: false,
  });

  const setChatOpen = (open: boolean) => {
    setState(prev => ({ ...prev, chatOpen: open }));
  };

  const setChatMinimized = (minimized: boolean) => {
    setState(prev => ({ ...prev, chatMinimized: minimized }));
  };

  const getOptimalChatPosition = () => {
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Mobile : position simple
      return {
        bottom: '1rem',
        right: '1rem',
        maxWidth: 'calc(100vw - 2rem)',
      };
    }

    // Position par défaut
    return {
      bottom: '1.5rem',
      right: '1.5rem',
      maxWidth: 'calc(100vw - 2rem)',
    };
  };

  return (
    <FloatingComponentsContext.Provider value={{
      state,
      setChatOpen,
      setChatMinimized,
      getOptimalChatPosition,
    }}>
      {children}
    </FloatingComponentsContext.Provider>
  );
}

export function useFloatingComponents() {
  const context = useContext(FloatingComponentsContext);
  if (context === undefined) {
    throw new Error('useFloatingComponents must be used within a FloatingComponentsProvider');
  }
  return context;
}
